import React, { useState, useMemo, useCallback } from 'react';
import { useNavigation } from '../contexts/NavigationProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useMessaging } from '../contexts/MessagingProvider';
import { useAuth } from '../contexts/AuthProvider';
import { useUI } from '../contexts/UIProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import type { Application } from '../types';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import ChatBubbleIcon from './icons/ChatBubbleIcon';
import UsersIcon from './icons/UsersIcon';
import CalendarIcon from './icons/CalendarIcon';
import BriefcaseIcon from './icons/BriefcaseIcon';
import StarIcon from './icons/StarIcon';
import CheckIcon from './icons/CheckIcon';
import CloseIcon from './icons/CloseIcon';
import PlaneIcon from './icons/PlaneIcon';
import { isWithinInterval, parseISO } from 'date-fns';

const ApplicantListPage: React.FC = () => {
    const { viewingOpportunity, handleNavigation, handleViewProfile, handleViewBand, bands, handleViewLineupEditor } = useNavigation();
    const { acceptApplicant, rejectApplication, shortlistApplication, closeOpportunity, opportunities } = useOpportunities();
    const { setActiveConversationPartner } = useMessaging();
    const { showToast } = useUI();
    const { users } = useAuth();
    
    const [selectedApplicants, setSelectedApplicants] = useState<string[]>([]);

    const targetDateKey = useMemo(() => {
        if (!viewingOpportunity?.startDate) return null;
        return viewingOpportunity.startDate.substring(0, 10);
    }, [viewingOpportunity]);

    const getApplicantStatus = useCallback((applicant: Application['applicant']): 'available' | 'day_off' | 'busy' | 'neutral' => {
        if (!targetDateKey || !viewingOpportunity) return 'neutral';

        const liveApplicant = users.find(u => u.handle === applicant.handle) || applicant;

        const explicitStatus = liveApplicant.availability?.[targetDateKey];
        if (explicitStatus === 'available') return 'available';
        if (explicitStatus === 'day_off') return 'day_off';

        const targetDateObj = parseISO(targetDateKey);
        
        const isBusy = opportunities.some(opp => {
            if (opp.id === viewingOpportunity.id) return false;
            if (!opp.startDate) return false;

            const start = new Date(`${opp.startDate}T00:00:00`);
            const end = opp.endDate ? new Date(`${opp.endDate}T00:00:00`) : start;

            const isOverlapping = isWithinInterval(targetDateObj, { start, end });
            if (!isOverlapping) return false;

            const isAuthor = opp.author.handle === applicant.handle;
            const isAccepted = opp.applications?.some(app => app.applicant.handle === applicant.handle && app.status === 'accepted');

            return isAuthor || isAccepted;
        });

        if (isBusy) return 'busy';

        return 'neutral';
    }, [targetDateKey, viewingOpportunity, users, opportunities]);

    const isApplicantTraveling = useCallback((applicant: Application['applicant']): boolean => {
        if (!viewingOpportunity || !targetDateKey) return false;
        
        const liveApplicant = users.find(u => u.handle === applicant.handle) || applicant;
        const oppCity = viewingOpportunity.location.toLowerCase().split(',')[0].trim();
        const homeCity = liveApplicant.city?.toLowerCase().split(',')[0].trim() || '';
        
        if (homeCity === oppCity) return false;
        
        return (liveApplicant.travelPlans || []).some(plan => {
            const planCity = plan.city.toLowerCase().split(',')[0].trim();
            if (planCity !== oppCity) return false;
            
            const targetDateObj = parseISO(targetDateKey);
            const planStart = new Date(`${plan.startDate}T00:00:00`);
            const planEnd = new Date(`${plan.endDate}T00:00:00`);
            
            return isWithinInterval(targetDateObj, { start: planStart, end: planEnd });
        });
    }, [viewingOpportunity, targetDateKey, users]);

    const { shortlistedApps, submittedApps, acceptedApps } = useMemo(() => {
        if (!viewingOpportunity) return { shortlistedApps: [], submittedApps: [], acceptedApps: [] };

        const allApps = (viewingOpportunity.applications || []).filter((app: Application) => app.status !== 'draft' && app.status !== 'rejected');
        
        const accepted = allApps.filter((app: Application) => app.status === 'accepted');
        const shortlisted = allApps.filter((app: Application) => app.status === 'shortlisted');
        const submitted = allApps.filter((app: Application) => app.status === 'submitted' || app.status === 'reviewed');

        const getPriority = (status: string) => {
            switch(status) {
                case 'available': return 0;
                case 'neutral': return 1;
                case 'busy': return 2;
                case 'day_off': return 3;
                default: return 1;
            }
        };

        const sortFn = (a: Application, b: Application) => {
            const statusA = getApplicantStatus(a.applicant);
            const statusB = getApplicantStatus(b.applicant);
            return getPriority(statusA) - getPriority(statusB);
        };

        return {
            acceptedApps: accepted,
            shortlistedApps: shortlisted.sort(sortFn),
            submittedApps: submitted.sort(sortFn)
        };
    }, [viewingOpportunity, getApplicantStatus]);

    if (!viewingOpportunity) {
        return (
            <div className="text-center p-8 bg-brand-surface rounded-lg font-urbanist">
                <h2 className="text-xl font-bold">Opportunity Not Found</h2>
                <button onClick={() => handleNavigation('manageApplicants')} className="mt-4 bg-brand-orange text-white font-bold py-2 px-4 rounded-full uppercase tracking-widest text-xs">Back</button>
            </div>
        );
    }

    const handleSelect = (appId: string) => {
        setSelectedApplicants(prev => 
            prev.includes(appId) ? prev.filter(id => id !== appId) : [...prev, appId]
        );
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>, list: Application[]) => {
        if (e.target.checked) {
            setSelectedApplicants(list.map((app: Application) => app.id));
        } else {
            setSelectedApplicants([]);
        }
    };
    
    const onMessage = (app: Application) => {
        setActiveConversationPartner(app.applicant);
        handleNavigation('messages');
    };
    
    const onCreateLineup = () => {
        handleViewLineupEditor(viewingOpportunity);
    }

    const onBandClick = (bandId: string) => {
        const band = bands.find(b => b.id === bandId);
        if (band) {
            handleViewBand(band);
        }
    };
    
    const handleBulkShortlist = async () => {
        for (const appId of selectedApplicants) {
            await shortlistApplication(viewingOpportunity.id, appId);
        }
        setSelectedApplicants([]);
    };

    const handleManualClose = async () => {
        if (window.confirm("Are you sure you want to close this opportunity? No more applications will be accepted.")) {
            await closeOpportunity(viewingOpportunity.id);
            showToast("Opportunity closed.");
        }
    };

    const renderList = (title: string, apps: Application[]) => {
        if (apps.length === 0) return null;
        
        const isAllSelected = selectedApplicants.length > 0 && apps.every(app => selectedApplicants.includes(app.id));

        return (
            <div className="mb-8 font-urbanist">
                <h3 className="text-xl font-black text-white mb-4 flex items-center uppercase tracking-tighter">
                    {title === 'Shortlist' && <StarIcon className="h-5 w-5 text-yellow-400 mr-2" />}
                    {title === 'Accepted' && <CheckIcon className="h-5 w-5 text-green-400 mr-2" />}
                    {title} ({apps.length})
                </h3>
                <div className="space-y-4">
                    <div className="flex items-center bg-brand-surface rounded-lg p-4 border-b border-gray-700/50">
                        <input 
                            type="checkbox"
                            className="h-5 w-5 rounded bg-gray-700 border-gray-600 text-brand-orange focus:ring-brand-orange"
                            checked={isAllSelected}
                            onChange={(e) => handleSelectAll(e, apps)}
                            aria-label="Select all applicants"
                        />
                        <label className="ml-3 text-[10px] font-black text-brand-muted uppercase tracking-widest">
                            Select All
                        </label>
                    </div>
                    {apps.map((app: Application) => (
                        <ApplicantCard 
                            key={app.id} 
                            application={app} 
                            isSelected={selectedApplicants.includes(app.id)}
                            onSelect={() => handleSelect(app.id)}
                            onViewProfile={() => handleViewProfile(app.applicant)}
                            onAccept={() => acceptApplicant(viewingOpportunity.id, app.id)}
                            onReject={() => rejectApplication(viewingOpportunity.id, app.id)}
                            onShortlist={() => shortlistApplication(viewingOpportunity.id, app.id)}
                            onMessage={() => onMessage(app)}
                            onBandClick={onBandClick}
                            status={getApplicantStatus(app.applicant)}
                            isTraveling={isApplicantTraveling(app.applicant)}
                            isShortlisted={title === 'Shortlist'}
                            isAccepted={title === 'Accepted'}
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6 font-urbanist">
            <div className="flex justify-between items-center">
                <button onClick={() => handleNavigation('manageApplicants')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-black uppercase tracking-widest text-[10px]">
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span>Back to Opportunities</span>
                </button>
                {viewingOpportunity.status === 'open' && (
                    <button onClick={handleManualClose} className="flex items-center space-x-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 px-3 py-1.5 rounded-full text-[10px] font-black border border-red-600/50 uppercase tracking-widest">
                        <CloseIcon className="h-4 w-4" />
                        <span>Close Opportunity</span>
                    </button>
                )}
            </div>

            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                <h2 className="text-3xl font-black text-white tracking-tighter uppercase">Applicants for</h2>
                <p className="text-xl text-brand-orange font-bold uppercase">{viewingOpportunity.title}</p>
                {viewingOpportunity.startDate && (
                    <p className="text-xs text-brand-muted mt-2 flex items-center font-bold uppercase tracking-widest">
                        <CalendarIcon className="h-4 w-4 mr-1 text-brand-orange" />
                        {new Date(viewingOpportunity.startDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                )}
            </div>
            
            {acceptedApps.length === 0 && shortlistedApps.length === 0 && submittedApps.length === 0 ? (
                <div className="text-center p-12 bg-brand-surface rounded-lg text-brand-muted border border-dashed border-gray-800">
                    <p className="font-bold uppercase tracking-widest text-sm">No active applications yet.</p>
                </div>
            ) : (
                <>
                    {renderList('Accepted', acceptedApps)}
                    {renderList('Shortlist', shortlistedApps)}
                    {renderList('New Applicants', submittedApps)}
                </>
            )}

            {selectedApplicants.length > 0 && (
                <div className="sticky bottom-24 md:bottom-5 bg-blue-600 text-white rounded-2xl shadow-2xl p-4 flex items-center justify-between z-10 border border-blue-400">
                    <p className="font-black uppercase tracking-tighter text-lg">{selectedApplicants.length} selected</p>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={handleBulkShortlist} className="flex items-center space-x-2 bg-yellow-500 text-black hover:bg-yellow-400 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors">
                            <StarIcon className="h-4 w-4" />
                            <span>Shortlist</span>
                        </button>
                        <button className="flex items-center space-x-2 bg-white text-black hover:bg-gray-100 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors">
                            <ChatBubbleIcon className="h-4 w-4" />
                            <span>Message</span>
                        </button>
                        <button onClick={onCreateLineup} className="flex items-center space-x-2 bg-brand-dark text-white hover:bg-black px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors">
                            <UsersIcon className="h-4 w-4" />
                            <span>Lineup</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

interface ApplicantCardProps {
    application: Application;
    isSelected: boolean;
    onSelect: () => void;
    onViewProfile: () => void;
    onAccept: () => void;
    onReject: () => void;
    onShortlist: () => void;
    onMessage: () => void;
    onBandClick: (bandId: string) => void;
    status: 'available' | 'day_off' | 'busy' | 'neutral';
    isTraveling: boolean;
    isShortlisted: boolean;
    isAccepted?: boolean;
}
const ApplicantCard: React.FC<ApplicantCardProps> = ({ application, isSelected, onSelect, onViewProfile, onAccept, onReject, onShortlist, onMessage, onBandClick, status, isTraveling, isShortlisted, isAccepted }) => {
    const { applicant, applyingAs } = application;
    const { t } = useLanguage();
    
    // Robust Entity Identification logic
    // FALLBACK: If applyingAs is missing or empty, treat as individual applicant
    const isBandApplication = applyingAs?.bands && applyingAs.bands.length > 0;
    const band = isBandApplication ? applyingAs.bands[0] : null;

    return (
        <div className={`bg-brand-surface rounded-xl p-5 flex items-start space-x-4 transition-all ${isSelected ? 'ring-2 ring-brand-orange border-brand-orange/50' : 'border border-gray-800'} ${isShortlisted ? 'border-yellow-500/50 bg-yellow-500/5' : ''} ${isAccepted ? 'border-green-500/50 bg-green-500/5' : ''}`}>
            <input 
                type="checkbox"
                className="h-5 w-5 rounded bg-gray-700 border-gray-600 text-brand-orange focus:ring-brand-orange mt-1 cursor-pointer"
                checked={isSelected}
                onChange={onSelect}
                aria-label={`Select ${applicant.name}`}
            />
            <div className="flex-1">
                <div className="flex items-start space-x-4">
                    <img 
                        src={isBandApplication ? band?.id ? `https://picsum.photos/seed/${band.id}/100/100` : applicant.avatarUrl : applicant.avatarUrl} 
                        alt={applicant.name} 
                        className="h-16 w-16 rounded-xl cursor-pointer object-cover border-2 border-gray-800 hover:border-brand-orange transition-colors" 
                        onClick={onViewProfile} 
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            {isBandApplication && band ? (
                                <h3 className="text-lg font-black text-white cursor-pointer hover:text-brand-orange transition-colors uppercase tracking-tight" onClick={() => onBandClick(band.id)}>
                                    {band.name}
                                </h3>
                            ) : (
                                <h3 className="text-lg font-black text-white cursor-pointer hover:text-brand-orange transition-colors uppercase tracking-tight" onClick={onViewProfile}>
                                    {applicant.name}
                                </h3>
                            )}
                            
                            {status === 'available' && (
                                <span className="bg-green-500/10 text-green-400 text-[9px] font-black px-2 py-1 rounded-full flex items-center uppercase tracking-widest border border-green-500/20">
                                    <CalendarIcon className="h-3 w-3 mr-1" /> {t('AVAILABLE')}
                                </span>
                            )}
                            {status === 'day_off' && (
                                <span className="bg-red-500/10 text-red-400 text-[9px] font-black px-2 py-1 rounded-full flex items-center uppercase tracking-widest border border-red-500/20">
                                    <CalendarIcon className="h-3 w-3 mr-1" /> {t('DAY_OFF')}
                                </span>
                            )}
                            {status === 'busy' && (
                                <span className="bg-orange-500/10 text-orange-400 text-[9px] font-black px-2 py-1 rounded-full flex items-center uppercase tracking-widest border border-orange-500/20">
                                    <BriefcaseIcon className="h-3 w-3 mr-1" /> Busy
                                </span>
                            )}
                            {isTraveling && (
                                <span className="bg-blue-500/10 text-blue-400 text-[9px] font-black px-2 py-1 rounded-full flex items-center uppercase tracking-widest border border-blue-500/20" title={t('TRAVELING_DESC')}>
                                    <PlaneIcon className="h-3 w-3 mr-1" /> {t('TRAVELING_APPLICANT')}
                                </span>
                            )}
                            {isAccepted && (
                                <span className="bg-green-500 text-black text-[9px] font-black px-2 py-1 rounded-full flex items-center uppercase tracking-widest">
                                    <CheckIcon className="h-3 w-3 mr-1" /> Accepted
                                </span>
                            )}
                        </div>
                        
                        {isBandApplication ? (
                            <p className="text-xs text-brand-muted font-bold uppercase tracking-widest mt-1">Lead: {applicant.name}</p>
                        ) : (
                            <p className="text-sm text-brand-orange font-bold truncate">{applicant.title}</p>
                        )}
                        <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest mt-1">Base: {applicant.city || 'Global'}</p>
                    </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-800/50">
                    <h4 className="text-[10px] font-black text-brand-muted mb-2 uppercase tracking-[0.2em]">Cover Note</h4>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed font-medium">{application.coverLetter || 'No cover letter provided.'}</p>
                </div>

                {application.submissionLinks && application.submissionLinks.length > 0 && (
                    <div className="mt-4">
                        <h4 className="text-[10px] font-black text-brand-muted mb-2 uppercase tracking-[0.2em]">Showcase Links</h4>
                        <div className="flex flex-wrap gap-2">
                            {application.submissionLinks.map((link, index) => (
                                <a key={index} href={link} target="_blank" rel="noopener noreferrer" className="text-[10px] bg-gray-800 text-brand-muted hover:text-white px-3 py-1 rounded-lg border border-gray-700 transition-colors truncate max-w-[200px] font-bold uppercase tracking-widest">
                                    {link.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-6 pt-4 border-t border-gray-800/50 flex flex-wrap gap-2">
                    {!isShortlisted && !isAccepted && (
                        <button onClick={onShortlist} className="bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500 hover:text-black font-black px-5 py-2.5 rounded-full text-[10px] flex items-center uppercase tracking-widest transition-all border border-yellow-500/30">
                            <StarIcon className="h-4 w-4 mr-1.5" /> Shortlist
                        </button>
                    )}
                    <button onClick={onMessage} className="bg-gray-800 text-white hover:bg-gray-700 font-black px-5 py-2.5 rounded-full text-[10px] uppercase tracking-widest transition-all border border-gray-700">
                        Message
                    </button>
                    {!isAccepted && (
                        <>
                            <button onClick={onAccept} className="bg-green-600 text-white hover:bg-green-500 font-black px-6 py-2.5 rounded-full text-[10px] uppercase tracking-widest transition-all shadow-lg shadow-green-500/10 ml-auto">
                                Accept
                            </button>
                            <button onClick={onReject} className="bg-red-600/10 text-red-500 hover:bg-red-600 hover:text-white font-black px-6 py-2.5 rounded-full text-[10px] uppercase tracking-widest transition-all border border-red-600/30">
                                Reject
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApplicantListPage;
